import React, { useState, useRef } from 'react';

function extractColors(img, count = 8) {
  const canvas = document.createElement('canvas');
  const S = 120;
  canvas.width = S; canvas.height = S;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, S, S);
  const data = ctx.getImageData(0, 0, S, S).data;

  const pixels = [];
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 80) continue;
    pixels.push([data[i], data[i + 1], data[i + 2]]);
  }
  if (pixels.length === 0) return [];

  // k-means
  let centroids = [];
  const step = Math.floor(pixels.length / count);
  for (let i = 0; i < count; i++) centroids.push([...pixels[Math.min(i * step, pixels.length - 1)]]);

  for (let iter = 0; iter < 12; iter++) {
    const clusters = Array.from({ length: count }, () => []);
    for (const px of pixels) {
      let minD = Infinity, ci = 0;
      for (let j = 0; j < count; j++) {
        const d = (px[0]-centroids[j][0])**2 + (px[1]-centroids[j][1])**2 + (px[2]-centroids[j][2])**2;
        if (d < minD) { minD = d; ci = j; }
      }
      clusters[ci].push(px);
    }
    for (let j = 0; j < count; j++) {
      if (!clusters[j].length) continue;
      centroids[j] = [
        Math.round(clusters[j].reduce((a, p) => a + p[0], 0) / clusters[j].length),
        Math.round(clusters[j].reduce((a, p) => a + p[1], 0) / clusters[j].length),
        Math.round(clusters[j].reduce((a, p) => a + p[2], 0) / clusters[j].length),
      ];
    }
  }

  // deduplicate similar colors
  const unique = [];
  for (const c of centroids) {
    const hex = '#' + c.map(v => v.toString(16).padStart(2, '0')).join('');
    if (!unique.some(u => {
      const d = Math.hypot(...u.rgb.map((v, i) => v - c[i]));
      return d < 30;
    })) {
      unique.push({ hex, rgb: c, lum: 0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2] });
    }
  }
  return unique.sort((a, b) => b.lum - a.lum).slice(0, 8);
}

export default function BrandPaletteSection() {
  const [colors,  setColors]  = useState([]);
  const [preview, setPreview] = useState(null);
  const [copied,  setCopied]  = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  const handleFile = (file) => {
    if (!file) return;
    setLoading(true);
    setColors([]);
    const url = URL.createObjectURL(file);
    setPreview(url);
    const img = new Image();
    img.onload = () => {
      const extracted = extractColors(img);
      setColors(extracted);
      setLoading(false);
    };
    img.onerror = () => setLoading(false);
    img.src = url;
  };

  const copy = (hex) => {
    navigator.clipboard.writeText(hex);
    setCopied(hex);
    setTimeout(() => setCopied(null), 1600);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) handleFile(file);
  };

  return (
    <div style={{ animation:'fadeIn 0.3s ease' }}>
      <div style={{ marginBottom:32 }}>
        <div style={{ fontSize:'0.65rem', color:'#555', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:6 }}>Outil gratuit</div>
        <h2 style={{ fontSize:'1.6rem', fontWeight:900, color:'#EAEAEA', letterSpacing:'-0.03em', marginBottom:4 }}>Palette de marque</h2>
        <p style={{ color:'#555', fontSize:'0.85rem' }}>Extrayez les couleurs dominantes d'un logo ou d'une image — 100% local, aucun upload.</p>
      </div>

      {/* DROP ZONE */}
      <div
        onClick={() => fileRef.current.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={onDrop}
        style={{
          border:`2px dashed ${preview ? 'rgba(200,255,0,0.2)' : 'rgba(255,255,255,0.07)'}`,
          borderRadius:16, padding:'36px 24px', textAlign:'center', cursor:'pointer',
          marginBottom:28, background:'rgba(255,255,255,0.01)', transition:'border-color 0.2s',
        }}
      >
        <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }} onChange={e => handleFile(e.target.files?.[0])} />
        {preview ? (
          <img src={preview} alt="Aperçu" style={{ maxHeight:120, maxWidth:280, borderRadius:8, objectFit:'contain' }} />
        ) : (
          <>
            <svg width="40" height="40" fill="none" stroke="#2a2a2a" strokeWidth="1.5" viewBox="0 0 24 24" style={{ marginBottom:12 }}>
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
            <div style={{ color:'#444', fontSize:'0.88rem', marginBottom:4 }}>Glissez un logo ou cliquez pour choisir</div>
            <div style={{ color:'#2a2a2a', fontSize:'0.75rem' }}>PNG · JPG · SVG · WEBP — traitement 100% local</div>
          </>
        )}
      </div>

      {/* LOADING */}
      {loading && (
        <div style={{ display:'flex', alignItems:'center', gap:10, color:'#444', fontSize:'0.84rem', marginBottom:20 }}>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <div style={{ width:16, height:16, border:'2px solid rgba(200,255,0,0.15)', borderTop:'2px solid #C8FF00', borderRadius:'50%', animation:'spin 0.7s linear infinite' }}/>
          Extraction des couleurs…
        </div>
      )}

      {/* PALETTE */}
      {colors.length > 0 && (
        <>
          {/* GRADIENT BAR */}
          <div style={{ display:'flex', height:48, borderRadius:12, overflow:'hidden', marginBottom:20, gap:1 }}>
            {colors.map((c, i) => (
              <div
                key={i} title={c.hex}
                onClick={() => copy(c.hex)}
                style={{ flex:1, background:c.hex, cursor:'pointer', transition:'flex 0.2s' }}
              />
            ))}
          </div>

          {/* SWATCHES */}
          <div style={{ fontSize:'0.65rem', color:'#555', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:12 }}>Couleurs extraites — cliquez pour copier</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:10, marginBottom:16 }}>
            {colors.map((c, i) => (
              <div
                key={i} onClick={() => copy(c.hex)}
                style={{ cursor:'pointer', borderRadius:12, overflow:'hidden', border:'1px solid rgba(255,255,255,0.06)', transition:'transform 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                <div style={{ height:72, background:c.hex }} />
                <div style={{ background:'#111', padding:'10px 12px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{ fontFamily:'monospace', fontSize:'0.76rem', color:'#888', fontWeight:700, letterSpacing:'0.04em' }}>{c.hex.toUpperCase()}</span>
                  <span style={{ fontSize:'0.7rem', color: copied === c.hex ? '#C8FF00' : '#333', transition:'color 0.2s' }}>
                    {copied === c.hex ? '✓' : '⎘'}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            <button
              onClick={() => { setColors([]); setPreview(null); fileRef.current.value = ''; }}
              style={{ background:'transparent', border:'1px solid rgba(255,255,255,0.1)', color:'#888', padding:'9px 18px', borderRadius:9, fontWeight:600, fontSize:'0.82rem', cursor:'pointer' }}
            >
              Nouvelle image
            </button>
            <button
              onClick={() => { const all = colors.map(c => c.hex.toUpperCase()).join(', '); navigator.clipboard.writeText(all); setCopied('all'); setTimeout(()=>setCopied(null),1600); }}
              style={{ background:'transparent', border:'1px solid rgba(255,255,255,0.1)', color:'#888', padding:'9px 18px', borderRadius:9, fontWeight:600, fontSize:'0.82rem', cursor:'pointer' }}
            >
              {copied === 'all' ? '✓ Tout copié !' : 'Copier tout'}
            </button>
          </div>
          <div style={{ marginTop:12, fontSize:'0.7rem', color:'#2a2a2a' }}>Traitement via Canvas API — aucune image envoyée au serveur</div>
        </>
      )}
    </div>
  );
}
